import { Flag, FolderOpen, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Button as AriaButton, Link as AriaLink, Separator } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Outlet, type Params, matchPath, useLocation, useMatches, useNavigationType, useParams } from 'react-router';

import { ApiError, WorkspaceIdContext } from '@/api/client.ts';
import {
    clientPath,
    clientPeoplePath,
    clientSettingsPath,
    clientTeamPath,
    projectClientPath,
    projectPath,
    projectSettingsPath,
    projectTeamPath,
} from '@/api/clients.ts';
import { useListAllProjects, useListClients } from '@/api/generated/clients.ts';
import { useGetMe, useSignOut } from '@/api/generated/users.ts';
import { useListWorkspaces } from '@/api/generated/workspaces.ts';
import { useWorkspace, useWorkspacePath } from '@/api/workspaces.ts';
import type { Layout } from '@/appearance/themes.ts';
import useAppearance from '@/appearance/useAppearance.ts';
import { ClientStarButton, ProjectStarButton } from '@/clients/components/StarButton.tsx';
import Avatar from '@/core/components/Avatar.tsx';
import Badge from '@/core/components/Badge.tsx';
import Breadcrumbs from '@/core/components/Breadcrumbs.tsx';
import Tooltip from '@/core/components/Tooltip.tsx';
import TopBar from '@/core/components/TopBar.tsx';
import useHasConnectionsTab from '@/settings/useHasConnectionsTab.ts';
import ReportProblemDialog from '@/support/ReportProblemDialog.tsx';
import { classnames } from '@/utils/classnames.ts';
import useLocalStorageState from '@/utils/useLocalStorageState.ts';
import AccessDisabledPage from '@/workspaces/AccessDisabledPage.tsx';
import WorkspaceSwitcher from '@/workspaces/components/WorkspaceSwitcher.tsx';
import { writeLastWorkspaceId } from '@/workspaces/lastWorkspace.ts';

import AppLogo from './AppLogo.tsx';
import {
    EMPLOYEE_NAV_ITEMS,
    type NavItem,
    PERSONAL_NAV_ITEMS,
    SETTINGS_NAV_ITEMS,
    WORKSPACE_NAV_ITEMS,
} from './navigation.ts';
import useRailMarks, { type RailMark } from './useRailMarks.ts';

export interface RouteHandle {
    crumb: (params: Params) => React.ReactNode;
    // Crumbs before the route's own that its URL doesn't hold, e.g. a recap's client and project; renders
    // `Breadcrumbs.Item`s.
    parentCrumbs?: (params: Params) => React.ReactNode;
    // The same for a client's employee, read from their endpoints (plan/DECISIONS.md D61); a route without one shows
    // no crumb to them.
    employeeCrumb?: (params: Params) => React.ReactNode;
    employeeParentCrumbs?: (params: Params) => React.ReactNode;
}

type Shell = Layout['shell'];

const railItemClassName =
    'flex size-(--rail-item) cursor-pointer items-center justify-center rounded-xl text-text-muted outline-none transition-colors hovered:bg-nav-hover hovered:text-text data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[disabled]:text-text-subtle';

// Expanded, items stretch to the full width. The icon keeps its box of the collapsed item's size (see RailLink), so it
// stays in place whatever its size (nav icons, the avatar).
const railItemExpandedClassName = 'md:w-full md:justify-start md:gap-0.5';

const railLabelClassName = 'hidden truncate text-sm font-medium text-text md:inline';

const SIDEBAR_STATES = ['collapsed', 'expanded'] as const;

// On the icon's corner where the item is only its icon (collapsed, and in the bar on phones), at the end of its row
// where it has its label, in a box as wide as a count so dots line up with the counts above and below them.
const RailBadge = ({ mark, isExpanded }: { mark: RailMark; isExpanded: boolean }) => {
    const badgeProps = {
        count: mark.count,
        isEmphasized: mark.isNew,
        tone: mark.isAlert ? 'negative' : 'neutral',
    } as const;
    return (
        <>
            <Badge
                {...badgeProps}
                className={classnames('absolute -top-0.5 -right-0.5 ring-2 ring-surface', isExpanded && 'md:hidden')}
            />
            {isExpanded && (
                <span className="mr-2.5 ml-auto flex min-w-4.5 justify-center max-md:hidden">
                    <Badge {...badgeProps} />
                </span>
            )}
        </>
    );
};

const withMark = (label: string, mark: RailMark | undefined) => (mark ? `${label}, ${mark.description}` : label);

interface RailLinkProps {
    path: string;
    label: string;
    icon: React.ReactNode;
    // Shown next to the icon in the expanded rail, where it replaces the tooltip; defaults to the label.
    expandedLabel?: React.ReactNode;
    isEnd?: boolean;
    // More paths it's active on (NavItem.subPaths), already with the workspace.
    subPaths?: string[];
    isInMobileBar: boolean;
    isExpanded: boolean;
    mark?: RailMark;
    className?: string;
}

const RailLink = ({
    path,
    label,
    icon,
    expandedLabel = label,
    isEnd,
    subPaths = [],
    isInMobileBar,
    isExpanded,
    mark,
    className,
}: RailLinkProps) => {
    const { pathname } = useLocation();
    const isActive =
        matchPath({ path, end: isEnd }, pathname) !== null ||
        subPaths.some((subPath) => matchPath(subPath, pathname) !== null);

    return (
        <Tooltip label={withMark(label, mark)} placement="end" isDisabled={isExpanded}>
            <AriaLink
                href={path}
                aria-label={withMark(label, mark)}
                aria-current={isActive ? 'page' : undefined}
                className={classnames(
                    'group relative',
                    railItemClassName,
                    isExpanded && railItemExpandedClassName,
                    !isInMobileBar && 'max-md:hidden',
                    isActive && 'bg-nav-active text-nav-active-text hovered:bg-nav-active hovered:text-nav-active-text',
                    className
                )}
            >
                <span className="flex size-(--rail-item) shrink-0 items-center justify-center">{icon}</span>
                {isExpanded && (
                    <span className={classnames(railLabelClassName, isActive && 'text-nav-active-text')}>
                        {expandedLabel}
                    </span>
                )}
                {mark && <RailBadge mark={mark} isExpanded={isExpanded} />}
            </AriaLink>
        </Tooltip>
    );
};

const RailNavLink = ({ item, isExpanded, mark }: { item: NavItem; isExpanded: boolean; mark?: RailMark }) => {
    const { t } = useTranslation();
    const workspacePath = useWorkspacePath();
    const Icon = item.icon;

    return (
        <RailLink
            path={workspacePath(item.path)}
            label={t(`nav.${item.navKey}`)}
            icon={<Icon className="shrink-0" />}
            isEnd={item.isEnd}
            subPaths={item.subPaths?.map(workspacePath)}
            isInMobileBar={item.isInMobileBar}
            isExpanded={isExpanded}
            mark={mark}
        />
    );
};

// The collapsed rail has no room for the heading, so a line keeps the sections apart there.
const RailSectionHeading = ({ label, isExpanded }: { label: string; isExpanded: boolean }) =>
    isExpanded ? (
        <div className="mt-3 hidden w-full truncate px-2.5 text-xs font-medium text-text-subtle short:px-2 md:block">
            {label}
        </div>
    ) : (
        <Separator className="my-1.5 hidden w-full border-t border-border md:block" />
    );

// A favorite's row. While it's hovered in the expanded rail, or its star has focus, the star stands in for the logo, to
// take it out of the favorites right there; it's a button of its own beside the link, which can't hold one. Folded, the
// row is only its logo, so it stays a link.
const RailFavorite = ({ star, ...props }: Omit<RailLinkProps, 'isInMobileBar'> & { star: React.ReactNode }) => (
    <div className="group/favorite relative w-full">
        <RailLink
            {...props}
            icon={
                <span
                    className={classnames(
                        'flex',
                        props.isExpanded &&
                            'group-hover/favorite:invisible group-has-[button[data-focus-visible]]/favorite:invisible'
                    )}
                >
                    {props.icon}
                </span>
            }
            isInMobileBar={false}
            className="group-hover/favorite:bg-nav-hover group-hover/favorite:text-text"
        />
        {props.isExpanded && star}
    </div>
);

// Over the logo's box, shown only while the row is hovered or the star has focus.
const favoriteStarClassName =
    'absolute top-0.5 left-0.5 opacity-0 group-hover/favorite:opacity-100 data-[focus-visible]:opacity-100';

// The clients and projects the member starred (plan/DECISIONS.md D127), by client and then by name: a starred client,
// then its starred projects, each with its own logo or else the client's. Under their starred client, expanded, projects
// show a vertical line in its place, down from the client's logo; folded, the logo is all a row has. The team's own
// projects (D184) come last, with their logo or a folder.
const RailFavorites = ({ isExpanded }: { isExpanded: boolean }) => {
    const { t } = useTranslation();
    const workspacePath = useWorkspacePath();
    const clients = useListClients();
    const projects = useListAllProjects();
    const favorites = (clients.data ?? []).filter(
        (client) => client.is_starred || client.projects.some((project) => project.is_starred)
    );
    const ownFavorites = (projects.data ?? []).filter((project) => project.client === null && project.is_starred);
    const changesMark = { count: 0, isNew: true, isAlert: false, description: t('clients.hasChanges') };

    if (favorites.length === 0 && ownFavorites.length === 0) return null;
    return (
        <>
            <RailSectionHeading label={t('nav.favorites')} isExpanded={isExpanded} />
            <div className="hidden w-full flex-col gap-1.5 short:gap-1 md:flex">
                {favorites.map((client) => {
                    const logo = <Avatar name={client.name} src={client.logo_url ?? undefined} size="2xs" />;
                    // A row's height and the gap to the next, so the lines of its projects join up.
                    const branch = <span aria-hidden className="h-10.5 w-px bg-border short:h-9" />;
                    return (
                        <Fragment key={client.id}>
                            {client.is_starred && (
                                <RailFavorite
                                    path={workspacePath(clientPath(client))}
                                    label={client.name}
                                    icon={logo}
                                    isEnd
                                    subPaths={[
                                        workspacePath(clientTeamPath(client)),
                                        workspacePath(clientPeoplePath(client)),
                                        workspacePath(clientSettingsPath(client)),
                                    ]}
                                    isExpanded={isExpanded}
                                    mark={client.has_changes ? changesMark : undefined}
                                    star={<ClientStarButton client={client} className={favoriteStarClassName} />}
                                />
                            )}
                            {client.projects
                                .filter((project) => project.is_starred)
                                .map((project) => (
                                    <RailFavorite
                                        key={project.id}
                                        path={workspacePath(projectPath(project))}
                                        label={`${project.name} · ${client.name}`}
                                        expandedLabel={project.name}
                                        icon={
                                            client.is_starred && isExpanded ? (
                                                branch
                                            ) : project.logo_url ? (
                                                <Avatar name={project.name} src={project.logo_url} size="2xs" />
                                            ) : (
                                                logo
                                            )
                                        }
                                        isEnd
                                        subPaths={[
                                            workspacePath(projectTeamPath(project)),
                                            workspacePath(projectClientPath(project)),
                                            workspacePath(projectSettingsPath(project)),
                                        ]}
                                        isExpanded={isExpanded}
                                        mark={project.has_changes ? changesMark : undefined}
                                        star={<ProjectStarButton project={project} className={favoriteStarClassName} />}
                                    />
                                ))}
                        </Fragment>
                    );
                })}
                {ownFavorites.map((project) => (
                    <RailFavorite
                        key={project.id}
                        path={workspacePath(projectPath(project))}
                        label={project.name}
                        icon={
                            <Avatar
                                name={project.name}
                                src={project.logo_url ?? undefined}
                                icon={<FolderOpen />}
                                size="2xs"
                            />
                        }
                        isEnd
                        subPaths={[
                            workspacePath(projectTeamPath(project)),
                            workspacePath(projectClientPath(project)),
                            workspacePath(projectSettingsPath(project)),
                        ]}
                        isExpanded={isExpanded}
                        mark={project.has_changes ? changesMark : undefined}
                        star={<ProjectStarButton project={project} className={favoriteStarClassName} />}
                    />
                ))}
            </div>
        </>
    );
};

// What's the user's own, at the rail's bottom without a menu: reporting a problem (plan task 98) as an item of its own,
// then their avatar, name and address as the link to the account settings and signing out as an icon at the end of
// that row; folded, stacked. Experiment page experiments/user-menu, D2. On phones the avatar in the bar leads to the
// account settings, which have the other two.
const RailAccount = ({ isExpanded, mark }: { isExpanded: boolean; mark?: RailMark }) => {
    const { t } = useTranslation();
    const workspacePath = useWorkspacePath();
    const user = useGetMe();
    // Reloads into the sign-in, which drops every cached query and the app's state at once, instead of leaving pages
    // that refetch without a session.
    const signOut = useSignOut({ mutation: { onSuccess: () => window.location.assign('/sign-in') } });
    const name = user.data?.display_name ?? '';
    const avatarUrl = user.data?.avatar_url ?? undefined;
    const accountPath = workspacePath('/user-settings');
    // Also on its tabs and what's below them, e.g. Darstellung or one of the user's own connections.
    const accountSubPaths = [workspacePath('/user-settings/*')];
    const [isReportingProblem, setIsReportingProblem] = useState(false);

    return (
        <>
            <Tooltip label={t('support.title')} placement="end" isDisabled={isExpanded}>
                <AriaButton
                    aria-label={t('support.title')}
                    onPress={() => setIsReportingProblem(true)}
                    className={classnames(
                        railItemClassName,
                        isExpanded && railItemExpandedClassName,
                        'mb-1.5 max-md:hidden'
                    )}
                >
                    <span className="flex size-(--rail-item) shrink-0 items-center justify-center">
                        <Flag />
                    </span>
                    {isExpanded && <span className="truncate text-sm font-medium">{t('support.title')}</span>}
                </AriaButton>
            </Tooltip>
            <div
                className={classnames(
                    'flex w-full gap-1 max-md:hidden',
                    // Folded, the rail has no room around its items.
                    isExpanded ? 'p-1' : 'flex-col'
                )}
            >
                <div className={classnames(isExpanded && 'min-w-0 flex-1')}>
                    <RailLink
                        path={accountPath}
                        subPaths={accountSubPaths}
                        label={t('auth.account', { name })}
                        expandedLabel={
                            <span className="flex flex-col">
                                <span className="truncate">{name}</span>
                                <span className="truncate text-sm font-normal text-text-muted">{user.data?.email}</span>
                            </span>
                        }
                        icon={
                            <Avatar name={name} src={avatarUrl} size={isExpanded ? 'sm' : '2xs'} className="shrink-0" />
                        }
                        isInMobileBar={false}
                        isExpanded={isExpanded}
                        mark={mark}
                        className={classnames(isExpanded && 'p-1.5 pr-3 md:h-auto md:gap-2')}
                    />
                </div>
                <Tooltip label={t('auth.signOut')} placement="end">
                    <AriaButton
                        aria-label={t('auth.signOut')}
                        onPress={() => signOut.mutate()}
                        className={classnames(railItemClassName, 'shrink-0', isExpanded && 'md:h-auto')}
                    >
                        <LogOut />
                    </AriaButton>
                </Tooltip>
            </div>
            <RailLink
                path={accountPath}
                subPaths={accountSubPaths}
                label={t('auth.account', { name })}
                icon={<Avatar name={name} src={avatarUrl} size="2xs" className="shrink-0" />}
                isInMobileBar
                isExpanded={false}
                mark={mark}
                className="md:hidden"
            />
            <ReportProblemDialog isOpen={isReportingProblem} onClose={() => setIsReportingProblem(false)} />
        </>
    );
};

// Everything between the logo and the account, which stay in place while it scrolls. A line marks the edge that has
// more beyond it, in place of a scrollbar, which would squeeze the folded rail's icons. On phones it's only its items,
// in the bar.
const RailScrollArea = ({ children }: { children: React.ReactNode }) => {
    const ref = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [hasMoreAbove, setHasMoreAbove] = useState(false);
    const [hasMoreBelow, setHasMoreBelow] = useState(false);

    const measure = () => {
        const element = ref.current;
        if (!element) return;
        setHasMoreAbove(element.scrollTop > 0);
        setHasMoreBelow(element.scrollTop + element.clientHeight < element.scrollHeight - 1);
    };

    useEffect(() => {
        if (!ref.current || !contentRef.current) return;
        // The content also changes height without the area changing, e.g. once the favorites have loaded.
        const observer = new ResizeObserver(measure);
        observer.observe(ref.current);
        observer.observe(contentRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            onScroll={measure}
            className={classnames(
                'contents md:-mx-2.5 md:flex md:min-h-0 md:flex-1 md:flex-col md:self-stretch md:overflow-x-hidden md:overflow-y-auto md:border-y md:[scrollbar-width:none] md:border-transparent md:px-2.5 md:pt-4 md:pb-1',
                hasMoreAbove && 'md:border-t-border',
                hasMoreBelow && 'md:border-b-border'
            )}
        >
            <div ref={contentRef} className="contents md:flex md:flex-col md:items-start md:gap-1">
                {children}
            </div>
        </div>
    );
};

const Rail = ({ shell }: { shell?: Shell }) => {
    const { t } = useTranslation();
    const workspacePath = useWorkspacePath();
    const isEmployee = useWorkspace().role === 'employee';
    const [sidebarState, setSidebarState] = useLocalStorageState('sidebar', 'collapsed', SIDEBAR_STATES);
    const isExpanded = sidebarState === 'expanded';
    const marks = useRailMarks();
    const hasConnectionsTab = useHasConnectionsTab();
    const settingsNavItems = SETTINGS_NAV_ITEMS.filter((item) => item.navKey !== 'connections' || hasConnectionsTab);

    return (
        // The items' size is set here once, so what makes an item wider or taller (expanded, the account's row) still
        // overrides it on short screens.
        <nav
            className={classnames(
                'group [--rail-item:2.25rem] md:short:[--rail-item:2rem] fixed inset-x-0 bottom-0 z-30 flex h-14 items-center justify-around border-t border-border bg-rail md:static md:h-auto md:shrink-0 md:flex-col md:items-start md:justify-start md:gap-1 md:overflow-hidden md:border-t-0 md:border-r md:px-2.5 md:py-3 md:transition-[width]',
                isExpanded ? 'md:w-70' : 'md:w-14 md:short:w-13',
                shell === 'inset' && 'md:border-transparent',
                shell === 'dark-frame' && 'chrome-dark'
            )}
        >
            {isExpanded ? (
                <div className="hidden w-full items-center justify-between gap-2 md:flex">
                    <AriaLink
                        href={workspacePath('/')}
                        className="flex min-w-0 cursor-pointer items-center gap-3 rounded-lg outline-none data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary"
                    >
                        <AppLogo className="size-(--rail-item)" />
                        <span className="truncate text-sm font-semibold text-text">{t('app.name')}</span>
                    </AriaLink>
                    <Tooltip label={t('app.collapseSidebar')} placement="end">
                        <AriaButton
                            aria-label={t('app.collapseSidebar')}
                            onPress={() => setSidebarState('collapsed')}
                            className={railItemClassName}
                        >
                            <PanelLeftClose />
                        </AriaButton>
                    </Tooltip>
                </div>
            ) : (
                // The logo turns into the expand button while the rail is hovered or the button has keyboard focus.
                <Tooltip label={t('app.expandSidebar')} placement="end">
                    <AriaButton
                        aria-label={t('app.expandSidebar')}
                        onPress={() => setSidebarState('expanded')}
                        className={classnames(railItemClassName, 'max-md:hidden')}
                    >
                        <AppLogo className="size-(--rail-item) group-hover:hidden in-focus-visible:hidden" />
                        <PanelLeftOpen className="hidden group-hover:block in-focus-visible:block" />
                    </AriaButton>
                </Tooltip>
            )}
            <RailScrollArea>
                {isEmployee ? (
                    EMPLOYEE_NAV_ITEMS.map((item) => (
                        <RailNavLink key={item.navKey} item={item} isExpanded={isExpanded} mark={marks[item.navKey]} />
                    ))
                ) : (
                    <>
                        {PERSONAL_NAV_ITEMS.map((item) => (
                            <RailNavLink
                                key={item.navKey}
                                item={item}
                                isExpanded={isExpanded}
                                mark={marks[item.navKey]}
                            />
                        ))}
                        <RailFavorites isExpanded={isExpanded} />
                        <RailSectionHeading label={t('nav.workspace')} isExpanded={isExpanded} />
                        {WORKSPACE_NAV_ITEMS.map((item) => (
                            <RailNavLink
                                key={item.navKey}
                                item={item}
                                isExpanded={isExpanded}
                                mark={marks[item.navKey]}
                            />
                        ))}
                        <Separator className="my-1.5 hidden w-full border-t border-border md:block" />
                        {settingsNavItems.map((item) => (
                            <RailNavLink
                                key={item.navKey}
                                item={item}
                                isExpanded={isExpanded}
                                mark={marks[item.navKey]}
                            />
                        ))}
                    </>
                )}
            </RailScrollArea>
            <RailAccount isExpanded={isExpanded} mark={marks.user} />
        </nav>
    );
};

const AppTopBar = ({ shell }: { shell?: Shell }) => {
    const isEmployee = useWorkspace().role === 'employee';
    const crumbs = useMatches().flatMap((match) => {
        const handle = match.handle as RouteHandle | undefined;
        const crumb = isEmployee ? handle?.employeeCrumb : handle?.crumb;
        if (!crumb) return [];
        const parentCrumbs = isEmployee ? handle?.employeeParentCrumbs : handle?.parentCrumbs;
        return [{ match, crumb, parentCrumbs }];
    });

    return (
        <TopBar className={classnames(shell === 'dark-frame' && 'chrome-dark')}>
            <WorkspaceSwitcher />
            <Breadcrumbs.Separator />
            <Breadcrumbs>
                {crumbs.map(({ match, crumb, parentCrumbs }) => (
                    <Fragment key={match.id}>
                        {parentCrumbs?.(match.params)}
                        <Breadcrumbs.Item href={match.pathname}>{crumb(match.params)}</Breadcrumbs.Item>
                    </Fragment>
                ))}
            </Breadcrumbs>
        </TopBar>
    );
};

const AppLayout = () => {
    const { workspaceSlug } = useParams();
    const workspaces = useListWorkspaces();
    const workspace = workspaces.data?.find((item) => item.slug === workspaceSlug);

    // The id, not the slug: it stays the same when the slug is changed.
    useEffect(() => {
        if (workspace) writeLastWorkspaceId(workspace.id);
    }, [workspace]);

    // A link opens its page at the top. The page scrolls inside the frame's scrolling area, which react-router's <ScrollRestoration> doesn't
    // reach (it only scrolls the window). Filters replace the entry instead of adding one, so they don't jump, and going
    // back keeps where the page was. A link to a section (`#todos`) is scrolled to by its page.
    const location = useLocation();
    const navigationType = useNavigationType();
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (navigationType === 'PUSH' && !location.hash) scrollAreaRef.current?.scrollTo(0, 0);
    }, [location.key, location.hash, navigationType]);
    const { shell } = useAppearance();

    if (workspaces.error) throw workspaces.error;
    // Everything below looks up the workspace from the URL (useWorkspace), so it waits for the list. Right after a slug
    // change the list already has the new slug while the URL still has the old one, until the navigation finishes.
    if (!workspace && (!workspaces.data || workspaces.isFetching)) return null;
    if (!workspace) throw new ApiError(404, 'not_found', `Workspace ${workspaceSlug} not found`);
    if (workspace.is_access_disabled) return <AccessDisabledPage workspace={workspace} />;

    return (
        // The generated API hooks call the endpoints of this workspace.
        <WorkspaceIdContext value={workspace.id}>
            <div className={classnames('flex h-dvh', shell === 'inset' ? 'bg-rail' : 'bg-surface')}>
                <Rail shell={shell} />
                {/* The page as a sheet set into the rail's surface (plan task 120), bordered as a card is; it clips what
                    scrolls inside, so the scrollbar and the top bar's line keep to its rounded corners. */}
                <div
                    className={classnames(
                        'flex min-w-0 flex-1',
                        shell === 'inset' &&
                            'bg-surface md:my-2 md:mr-2 md:overflow-hidden md:rounded-card md:border md:border-border'
                    )}
                >
                    {/* The top bar scrolls along inside, so the page shows through it. Its gutter is kept, so the
                        page doesn't shift sideways when it grows a scrollbar, e.g. when a topic opens. Positioned, so
                        absolute elements inside (an `sr-only` label far down the page) scroll with it instead of making
                        the document itself taller and scroll too. Padded at the top by the bar's height, so a section
                        scrolled to isn't left under it. */}
                    <div
                        ref={scrollAreaRef}
                        data-scroll-area
                        className="relative flex min-w-0 flex-1 scroll-pt-14 flex-col overflow-y-auto pb-14 [scrollbar-gutter:stable] md:pb-0"
                    >
                        <AppTopBar shell={shell} />
                        <main className="flex-1">
                            {/* Keyed so switching workspaces remounts the page instead of keeping state (forms, upload
                                lists) that belongs to the other workspace. */}
                            <Fragment key={workspace.id}>
                                <Outlet />
                            </Fragment>
                        </main>
                    </div>
                </div>
            </div>
        </WorkspaceIdContext>
    );
};

export default AppLayout;
